import { create } from 'zustand';
import { createStorage } from '@/shared/storage';
import { createChatWorkspaceStorageKey } from './keys';
import { getTabId } from './ids';
import type {
  AgentId,
  ChatLoginScope,
  ChatViewSnapshot,
  ChatWorkspaceSnapshot,
  ThreadId,
} from './types';

const storage = createStorage('local');
const VIEW_PREFIX = 'agenthub:chat-view:v1:';

type ChatViewState = ChatViewSnapshot & {
  isHydrated: boolean;
  tabId: string;
  // Bumped every time the login session is (re)configured or cleared. Lets
  // URL↔store sync detect a session reset and re-assert the URL over a freshly
  // restored/cleared snapshot. Runtime-only; never persisted in the snapshot.
  sessionEpoch: number;
  // A thread whose creation has been accepted by the backend for this tab's
  // current turn, but whose *render selection* (agentThreads[agentId]) is
  // deliberately deferred until the run finishes — selecting immediately would
  // remount assistant-ui mid-stream and drop in-flight deltas (see
  // ChatWorkspace's handleThreadAccepted). urlSync's store→URL mirror still
  // needs to know about it so the address bar (and thus a hard reload) can
  // resolve straight to this thread instead of staying on a bare agent URL
  // that a reload would otherwise indistinguishably treat as "start a new
  // chat". Runtime-only: on reload the promoted URL itself is what a fresh
  // tab uses to recover, so this never needs to be persisted or restored.
  pendingAcceptedThreadIds: Record<AgentId, ThreadId | null>;
};

type ChatViewActions = {
  configureSession: (scope: ChatLoginScope) => void;
  clearSession: () => void;
  selectAgent: (agentId: AgentId | null) => void;
  selectThread: (agentId: AgentId, threadId: ThreadId | null) => void;
  markThreadAcceptedPending: (agentId: AgentId, threadId: ThreadId) => void;
  pruneAgents: (agentIds: readonly AgentId[]) => void;
  clearSelection: () => void;
  clearAllState: (resetGeneration?: number) => void;
  applyResetGeneration: (resetGeneration: number) => void;
};

type ViewStore = ChatViewState & ChatViewActions;

type RuntimeState = {
  scope: ChatLoginScope | null;
  key: string | null;
};

const runtime: RuntimeState = {
  scope: null,
  key: null,
};

function emptySnapshot(scope: ChatLoginScope): ChatViewSnapshot {
  return {
    version: 1,
    userId: scope.userId,
    loginSessionId: scope.loginSessionId,
    selectedAgentId: null,
    agentThreads: {},
    lastAppliedResetGeneration: 0,
  };
}

function viewStorageKey(scope: ChatLoginScope, tabId: string): string {
  return `${VIEW_PREFIX}${scope.userId}:${scope.loginSessionId}:${tabId}`;
}

function isViewSnapshot(value: unknown): value is ChatViewSnapshot {
  if (value === null || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  return record.version === 1
    && typeof record.userId === 'string'
    && typeof record.loginSessionId === 'string'
    && typeof record.agentThreads === 'object'
    && record.agentThreads !== null
    && typeof record.lastAppliedResetGeneration === 'number';
}

function isWorkspaceSnapshot(value: unknown): value is ChatWorkspaceSnapshot {
  if (value === null || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  return record.version === 2
    && typeof record.userId === 'string'
    && typeof record.loginSessionId === 'string'
    && typeof record.agents === 'object'
    && record.agents !== null
    && typeof record.resetGeneration === 'number';
}

function readStoredSnapshot(scope: ChatLoginScope, tabId: string): ChatViewSnapshot | null {
  const raw = storage.get<unknown>(viewStorageKey(scope, tabId), null);
  return isViewSnapshot(raw) ? raw : null;
}

function seedSnapshotFromWorkspace(scope: ChatLoginScope): ChatViewSnapshot {
  const raw = storage.get<unknown>(createChatWorkspaceStorageKey(scope), null);
  if (!isWorkspaceSnapshot(raw)) return emptySnapshot(scope);

  const agentThreads: Record<AgentId, ThreadId | null> = {};
  let selectedAgentId: AgentId | null = null;
  let latestTouchedAt = '';

  for (const [agentId, agent] of Object.entries(raw.agents)) {
    if (agent.activeThreadId === null) continue;
    agentThreads[agentId] = agent.activeThreadId;
    if (agent.lastTouchedAt > latestTouchedAt) {
      latestTouchedAt = agent.lastTouchedAt;
      selectedAgentId = agentId;
    }
  }

  return {
    version: 1,
    userId: scope.userId,
    loginSessionId: scope.loginSessionId,
    selectedAgentId,
    agentThreads,
    lastAppliedResetGeneration: raw.resetGeneration,
  };
}

function restoreActiveWorkspaceThreads(snapshot: ChatViewSnapshot, scope: ChatLoginScope): ChatViewSnapshot {
  const selectedAgentId = snapshot.selectedAgentId;
  if (!selectedAgentId || snapshot.agentThreads[selectedAgentId] != null) return snapshot;
  const raw = storage.get<unknown>(createChatWorkspaceStorageKey(scope), null);
  if (!isWorkspaceSnapshot(raw)) return snapshot;

  const activeThreadId = raw.agents[selectedAgentId]?.activeThreadId;
  if (!activeThreadId) return snapshot;
  return {
    ...snapshot,
    agentThreads: { ...snapshot.agentThreads, [selectedAgentId]: activeThreadId },
  };
}

function persistSnapshot(snapshot: ChatViewSnapshot, tabId: string): void {
  storage.set(viewStorageKey(snapshot, tabId), snapshot);
}

function cleanupViewKeys(scope: ChatLoginScope): void {
  if (typeof window === 'undefined') return;
  try {
    const { localStorage } = window;
    const userPrefix = `${VIEW_PREFIX}${scope.userId}:`;
    const currentTabId = getTabId();
    const currentKey = viewStorageKey(scope, currentTabId);
    for (let index = localStorage.length - 1; index >= 0; index -= 1) {
      const key = localStorage.key(index);
      if (!key || !key.startsWith(userPrefix) || key === currentKey) continue;
      const expectedPrefix = `${VIEW_PREFIX}${scope.userId}:${scope.loginSessionId}:`;
      if (!key.startsWith(expectedPrefix)) localStorage.removeItem(key);
    }
  } catch {
    // ignore storage access errors
  }
}

function currentSnapshot(state: ViewStore): ChatViewSnapshot | null {
  if (!state.userId || !state.loginSessionId) return null;
  return {
    version: 1,
    userId: state.userId,
    loginSessionId: state.loginSessionId,
    selectedAgentId: state.selectedAgentId,
    agentThreads: state.agentThreads,
    lastAppliedResetGeneration: state.lastAppliedResetGeneration,
  };
}

export const useChatViewStore = create<ViewStore>((set, get) => ({
  ...emptySnapshot({ userId: '', loginSessionId: '' }),
  userId: '',
  loginSessionId: '',
  isHydrated: false,
  tabId: getTabId(),
  sessionEpoch: 0,
  pendingAcceptedThreadIds: {},

  configureSession: (scope) => {
    runtime.scope = scope;
    runtime.key = viewStorageKey(scope, get().tabId);
    cleanupViewKeys(scope);
    const stored = readStoredSnapshot(scope, get().tabId) ?? seedSnapshotFromWorkspace(scope);
    const snapshot = restoreActiveWorkspaceThreads(stored, scope);
    persistSnapshot(snapshot, get().tabId);
    set((state) => ({
      ...snapshot,
      isHydrated: true,
      sessionEpoch: state.sessionEpoch + 1,
      pendingAcceptedThreadIds: {},
    }));
  },

  clearSession: () => {
    if (runtime.key) storage.remove(runtime.key);
    runtime.scope = null;
    runtime.key = null;
    set((state) => ({
      ...emptySnapshot({ userId: '', loginSessionId: '' }),
      userId: '',
      loginSessionId: '',
      isHydrated: false,
      sessionEpoch: state.sessionEpoch + 1,
      pendingAcceptedThreadIds: {},
    }));
  },

  selectAgent: (agentId) => {
    set((state) => {
      const next = {
        ...(currentSnapshot(state) ?? emptySnapshot(runtime.scope ?? { userId: '', loginSessionId: '' })),
        selectedAgentId: agentId,
      };
      if (runtime.scope) persistSnapshot(next, state.tabId);
      return next;
    });
  },

  markThreadAcceptedPending: (agentId, threadId) => {
    set((state) => ({
      pendingAcceptedThreadIds: { ...state.pendingAcceptedThreadIds, [agentId]: threadId },
    }));
  },

  selectThread: (agentId, threadId) => {
    set((state) => {
      const next = {
        ...(currentSnapshot(state) ?? emptySnapshot(runtime.scope ?? { userId: '', loginSessionId: '' })),
        selectedAgentId: agentId,
        agentThreads: {
          ...state.agentThreads,
          [agentId]: threadId,
        },
      };
      if (runtime.scope) persistSnapshot(next, state.tabId);
      // An explicit selection (real thread, new-chat click, or the URL→store
      // seed effect) is authoritative and supersedes any not-yet-selected
      // accept from a previous turn — otherwise a stale pending id could
      // resurrect a different thread's URL after this one deliberately moved
      // on (e.g. starting a fresh chat right after a still-streaming accept).
      const { [agentId]: _dropped, ...pendingAcceptedThreadIds } = state.pendingAcceptedThreadIds;
      return { ...next, pendingAcceptedThreadIds };
    });
  },

  pruneAgents: (agentIds) => {
    const allowed = new Set(agentIds);
    set((state) => {
      const nextAgentThreads = Object.fromEntries(
        Object.entries(state.agentThreads).filter(([agentId]) => allowed.has(agentId)),
      );
      const nextSelectedAgentId = state.selectedAgentId && allowed.has(state.selectedAgentId)
        ? state.selectedAgentId
        : null;
      const next = {
        ...(currentSnapshot(state) ?? emptySnapshot(runtime.scope ?? { userId: '', loginSessionId: '' })),
        selectedAgentId: nextSelectedAgentId,
        agentThreads: nextAgentThreads,
      };
      if (runtime.scope) persistSnapshot(next, state.tabId);
      return next;
    });
  },

  clearSelection: () => {
    set((state) => {
      const next = {
        ...(currentSnapshot(state) ?? emptySnapshot(runtime.scope ?? { userId: '', loginSessionId: '' })),
        selectedAgentId: null,
      };
      if (runtime.scope) persistSnapshot(next, state.tabId);
      return next;
    });
  },

  clearAllState: (resetGeneration = 0) => {
    set((state) => {
      const next = {
        ...(currentSnapshot(state) ?? emptySnapshot(runtime.scope ?? { userId: '', loginSessionId: '' })),
        selectedAgentId: null,
        agentThreads: {},
        lastAppliedResetGeneration: resetGeneration,
      };
      if (runtime.scope) persistSnapshot(next, state.tabId);
      return { ...next, pendingAcceptedThreadIds: {} };
    });
  },

  applyResetGeneration: (resetGeneration) => {
    const state = get();
    if (resetGeneration <= state.lastAppliedResetGeneration) return;
    get().clearAllState(resetGeneration);
  },
}));
